import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const SmarterRevenueDecisions = () => {
  // Structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Strive Product Operations & Revenue Intelligence",
    "description": "AI-powered product operations and revenue intelligence platform for smarter business decisions",
    "image": "https://strive.us/og-image.png",
    "video": {
      "@type": "VideoObject",
      "name": "Strive Platform Overview",
      "description": "Overview of Strive's AI-powered product operations platform",
      "thumbnailUrl": "https://customer-f5xkmr6krvr9p6pu.cloudflarestream.com/b922fd8fd0f091d8f616969e79def195/thumbnails/thumbnail.jpg",
      "uploadDate": "2025-03-16T00:00:00Z",
      "duration": "PT1M09S",
      "contentUrl": "https://customer-f5xkmr6krvr9p6pu.cloudflarestream.com/b922fd8fd0f091d8f616969e79def195/iframe"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "0",
        "priceCurrency": "USD"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "USD"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": "0",
            "maxValue": "1",
            "unitCode": "HUR"
          }
        }
      }
    },
    "hasMerchantReturnPolicy": {
      "@type": "MerchantReturnPolicy",
      "applicableCountry": "US",
      "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
      "merchantReturnDays": "30",
      "returnMethod": "https://schema.org/ReturnByMail",
      "returnFees": "https://schema.org/FreeReturn"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    },
    "review": {
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": "James P.",
        "jobTitle": "Product Strategy Lead"
      },
      "reviewBody": "Strive spots patterns in customer feedback that we would never have found manually.",
      "itemReviewed": {
        "@type": "Product",
        "name": "Strive Product Operations & Revenue Intelligence"
      }
    },
    "brand": {
      "@type": "Brand",
      "name": "Strive"
    },
    "featureList": [
      "Identify Key Patterns",
      "Connect All Data Sources",
      "Automate Intelligence Distribution"
    ]
  };

  return (
    <>
      <Helmet>
        <title>AI-Powered Product Operations & Revenue Intelligence | Strive</title>
        <meta name="description" content="Transform your product operations and revenue intelligence with AI-driven insights. Get a 360° view of customer needs, sales blockers, and market trends without manual effort." />
        <meta name="keywords" content="product operations, revenue intelligence, AI analytics, sales analytics, revenue optimization, business intelligence" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <section 
        id="revenue-intelligence" 
        className="py-16 md:py-28 relative overflow-hidden scroll-mt-[8rem]"
        aria-labelledby="revenue-intelligence-title"
      >
        {/* Background decoration - adjusted for mobile */}
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute right-0 top-1/4 w-1/2 md:w-1/3 h-1/3 bg-gradient-to-br from-slate-100/40 to-slate-200/30 rounded-full blur-3xl"></div>
          <div className="absolute left-1/4 top-1/2 w-1/3 md:w-1/4 h-1/4 bg-gradient-to-br from-blue-100/30 to-slate-200/20 rounded-full blur-3xl"></div>
          <div className="absolute left-0 bottom-0 w-1/2 md:w-1/3 h-1/4 bg-gradient-to-tr from-slate-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Header section */}
            <header className="text-center mb-8 md:mb-16">
              <h2 
                id="revenue-intelligence-title"
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4 max-w-2xl mx-auto"
              >
                Connect Product & Sales Teams
              </h2>
              
              <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
                Uncover hidden opportunities to increase revenue, save deals, reduce churn, and sell 
                what customers want to buy most by connecting your product and sales teams.
              </p>
            </header>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {/* Deal Intelligence Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mr-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-600">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Deal Intelligence</h3>
                </div>
                <p className="text-gray-600">
                  AI analyzes sales conversations and product feedback to identify why deals are won or lost, 
                  helping sales teams close more deals with the right messaging.
                </p>
              </div>

              {/* Churn Prevention Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center mr-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-green-600">
                      <path d="M17 21V19C17 16.7909 14.3137 15 11 15C7.68629 15 5 16.7909 5 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M11 12C13.2091 12 15 10.2091 15 8C15 5.79086 13.2091 4 11 4C8.79086 4 7 5.79086 7 8C7 10.2091 8.79086 12 11 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Churn Prevention</h3>
                </div>
                <p className="text-gray-600">
                  Early warning system detects at-risk customers by analyzing product usage, support tickets, 
                  and customer feedback, enabling proactive retention strategies.
                </p>
              </div>

              {/* Product-Sales Alignment Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mr-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-purple-600">
                      <path d="M21 21H4.6C4.03995 21 3.75992 21 3.54601 20.891C3.35785 20.7951 3.20487 20.6422 3.10899 20.454C3 20.2401 3 19.9601 3 19.4V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 7L16 12L13 9L9 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M17 7H21V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Product-Sales Alignment</h3>
                </div>
                <p className="text-gray-600">
                  Automatically surfaces the most requested features and common objections, helping product 
                  teams prioritize what will drive the most revenue.
                </p>
              </div>

              {/* Revenue Opportunities Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center mr-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-orange-600">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Revenue Opportunities</h3>
                </div>
                <p className="text-gray-600">
                  Identifies upsell opportunities and feature gaps by analyzing customer behavior and 
                  feedback, helping teams sell what customers want to buy most.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Ready to Drive Revenue Growth?
              </h3>
              <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
                Join leading companies using Strive to connect their product and sales teams, uncover 
                revenue opportunities, and reduce customer churn.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Get Started
                <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SmarterRevenueDecisions;